import React, {useCallback, useEffect, useState} from 'react'
import {Router, Route} from 'react-router-dom'
import ReactGA from 'react-ga'
import {createBrowserHistory} from 'history'
import {ThemeProvider} from '@mui/material/styles'
import theme from './theme'
import CssBaseline from '@mui/material/CssBaseline'
import Config from '../launch/config'
import Configure from '../configure/configure'
import Landing from '../landing/landing'
import Launch from '../launch/launch'
import Achievements from '../achievements/achievements'
import NotFound from '../404'
import Starbase from '../starbase/starbase'
import Demo from '../demo/demo'

import {getStoredState} from '../launch/store'

import './normalize.css'
import './grid.css'
import './table.css'
import './app.css'

export const AppContext = React.createContext()

export let _addToEventLog
export let _forceUpdate
export let _lastSelectedShipId
export let _selectedShipIds
export let _setConfig
export let _setGameLoading
export let _setGameLoadingPercent
export let _setGameOver
export let _setGameOverlayOpenState
export let _setMinimapOpen
export let _setIsMuted
export let _setIsPaused
export let _setIsSelectingShips
export let _setLastSelectedShipId
export let _setModalOpenState
export let _setSelectedEntityId
export let _setSelectedShipIds
export let _setSelectedTowableId
export let _togglePaused
export let _setHamburgerIsOpen

const AppProvider = (props) => {
    const [, updateState] = useState()
    const forceUpdate = useCallback(() => updateState({}), [])
    const [config, setConfig] = useState(Config)
    const [gameOverOverlayOpenState, setGameOverOverlayOpenState] = useState(
        false
    )
    const [minimapOpen, setMinimapOpen] = useState(false)
    const [modalOpenState, setModalOpenState] = useState(false)
    const [modalContentType, setModalContentType] = useState(null)
    const [lastSelectedShipId, setLastSelectedShipId] = useState(null)
    const [selectedEntityId, setSelectedEntityId] = useState(null)
    const [selectedShipIds, setSelectedShipIds] = useState({})
    const [selectedTowableId, setSelectedTowableId] = useState(null)
    const [isGameLoading, setGameLoading] = useState(true)
    const [gameLoadingPercent, setGameLoadingPercent] = useState(0)
    const [isGameOver, setGameOver] = useState(false)
    const [isSelectingShips, setIsSelectingShips] = useState(false)
    const [eventLog, addToEventLog] = useState([])
    const [isPaused, setIsPaused] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [wasMutedBeforePause, setWasMutedBeforePause] = useState(false)
    const [hamburgerIsOpen, setHamburgerIsOpen] = useState(false)

    useEffect(() => {
        const store = getStoredState()
        !!store?.config.isMuted && setIsMuted(store.config.isMuted)
    }, [])

    useEffect(() => {
        const store = getStoredState()
        if (!store) {
            return
        }
        Config.isMuted = isMuted
        store.config.isMuted = isMuted
        Config.scene.sound && Config.scene.sound.setMute(isMuted)
        window.localStorage.setItem(Config.namespace, JSON.stringify(store))
    }, [isMuted])

    _setHamburgerIsOpen = setHamburgerIsOpen
    _lastSelectedShipId = lastSelectedShipId
    _selectedShipIds = selectedShipIds
    _setConfig = setConfig
    _setGameOverlayOpenState = setGameOverOverlayOpenState
    _setLastSelectedShipId = setLastSelectedShipId
    _setMinimapOpen = setMinimapOpen
    _setModalOpenState = setModalOpenState
    _setSelectedEntityId = setSelectedEntityId
    _setSelectedShipIds = setSelectedShipIds
    _setSelectedTowableId = setSelectedTowableId
    _setGameLoading = (bool) => {
        setGameLoading(bool)
        Config.isGameLoading = bool
    }
    _setGameLoadingPercent = (value) => {
        setGameLoadingPercent(value)
    }
    _setGameOver = setGameOver
    _setIsMuted = setIsMuted
    _setIsPaused = setIsPaused
    _setIsSelectingShips = (bool) => {
        setIsSelectingShips(bool)
        Config.isSelectingShips = bool
    }
    _forceUpdate = forceUpdate
    _addToEventLog = (newValue) => {
        if (newValue) {
            newValue.timestamp = Date.now()
        }

        return addToEventLog((prevArray) =>
            newValue ? [...prevArray, newValue] : []
        )
    }

    // If user refreshes while paused we need to see if we're muted
    _togglePaused = (bool) => {
        setIsPaused(bool == null ? !isPaused : bool)

        if (!isPaused) {
            setWasMutedBeforePause(isMuted)
            setIsMuted(true)
        }

        if (isPaused) {
            if (!wasMutedBeforePause) {
                setIsMuted(false)
            }
        }
    }

    return (
        <AppContext.Provider
            value={{
                config,
                eventLog,
                gameLoadingPercent,
                hamburgerIsOpen,
                isGameLoading,
                isGameOver,
                isGameOverOverlayOpen: gameOverOverlayOpenState,
                isModalOpen: modalOpenState,
                isMuted,
                isPaused,
                isSelectingShips,
                lastSelectedShipId,
                modalContentType,
                minimapOpen,
                setIsMuted,
                setMinimapOpen,
                selectedEntityId,
                selectedShipIds,
                selectedTowableId,
                setIsSelectingShips,
                setGameLoading: (props) => setGameLoading(props),
                setGameLoadingPercent,
                setIsPaused,
                setGameOver: (props) => setGameOver(props),
                setModalContentType: (props) => {
                    setModalContentType(props)
                },
                setWasMutedBeforePause,
                toggleGameOverOverlay: (bool) =>
                    setGameOverOverlayOpenState(
                        bool == null ? !gameOverOverlayOpenState : bool
                    ),
                toggleModal: (bool) =>
                    setModalOpenState(bool == null ? !modalOpenState : bool),
                setModalOpenState,
                togglePaused: _togglePaused,
                setHamburgerIsOpen,
                wasMutedBeforePause,
            }}
        >
            {props.children}
        </AppContext.Provider>
    )
}

export const forceAppUpdate = () => {
    _forceUpdate()
}

export const handleBoardClick = () => {
    const entityId = Object.keys(Config.entities).find((key) => {
        return Config.entities[key].selected
    })

    _setSelectedEntityId(entityId)
    _setModalOpenState(false)
    _setGameOverlayOpenState(true)
    _setHamburgerIsOpen(false)
}

export const handleShipClick = (ship, close) => {
    ship && _setLastSelectedShipId(ship.id)

    if (!ship && Object.keys(_selectedShipIds).length === 1) {
        // After group selecting ships and then deselecting ships inidividually,
        // the last ship left selected is the last group selected ship, not the
        // ship left selected, sort that out here
        const selectedShipId = Object.keys(_selectedShipIds).find((key) => key)

        if (selectedShipId !== _lastSelectedShipId) {
            _setLastSelectedShipId(selectedShipId)
        }
    }

    _setModalOpenState(false)
    forceAppUpdate()
}

export const handleTowableClick = (towable, close) => {
    _setSelectedTowableId(towable ? towable.id : null)
    _setModalOpenState(false)
    forceAppUpdate()
}

ReactGA.initialize('UA-168702830-1')

const history = createBrowserHistory()

// Initialize google analytics page view tracking
history.listen((location) => {
    ReactGA.set({page: location.pathname}) // Update the user's current page
    ReactGA.pageview(location.pathname) // Record a pageview for the given page
})

const App = () => {
    const folder = '/ge'
    return (
        <AppProvider>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Router history={history}>
                    <div className="app">
                        <Route exact path={`${folder}/`} component={Landing} />
                        <Route
                            path={`${folder}/configure`}
                            component={Configure}
                        />
                        <Route path={`${folder}/launch`} component={Launch} />
                        <Route
                            path={`${folder}/launch-debug`}
                            component={Launch}
                        />
                        <Route
                            path={`${folder}/achievements`}
                            component={Achievements}
                        />
                        <Route
                            path={`${folder}/starbase`}
                            component={Starbase}
                        />
                        <Route path={`${folder}/demo`} component={Demo} />
                        <Route path={`${folder}/404`}>
                            <NotFound />
                        </Route>
                    </div>
                </Router>
            </ThemeProvider>
        </AppProvider>
    )
}

export default App
